/**
 * <StorageHealthSummary> — the Health tab's read-only storage summary
 * (JSS#1158), for Site Operator+.
 *
 * The full Storage panel (storage classes, per-node disks, the PVC table)
 * moved behind the Engineer-only Advanced tab. A Site Operator still needs
 * the operational read it gave them, "is any volume claim stuck?", so this
 * shows PVC counts by state and lists every claim that is not Bound. Same
 * query as StoragePanel (GET /storage), no filters, no controls.
 */
import { Database } from 'lucide-react';
import { useClusterStorage } from './useClusterData';

const STATE_CLASS: Record<string, string> = {
  Bound: 'text-status-healthy',
  Pending: 'text-status-warning',
  Lost: 'text-status-error',
};

export default function StorageHealthSummary({ siteId }: { siteId: string }) {
  const { data: storage, isLoading, error } = useClusterStorage(siteId);

  let body: JSX.Element;
  if (isLoading && !storage) {
    body = <div className="h-8 bg-surface-elevated rounded animate-pulse" />;
  } else if (error) {
    body = <p className="text-xs text-status-error">Failed to load storage: {(error as Error).message}</p>;
  } else {
    const pvcs = storage?.pvcs ?? [];
    const counts = new Map<string, number>();
    for (const p of pvcs) counts.set(p.status, (counts.get(p.status) ?? 0) + 1);
    const notBound = pvcs.filter((p) => p.status !== 'Bound');
    body = pvcs.length === 0 ? (
      <p className="text-xs text-silver-muted">No persistent volume claims on this site.</p>
    ) : (
      <div className="space-y-3">
        <div className="flex flex-wrap gap-4 text-sm" data-testid="storage-summary-counts">
          {Array.from(counts.entries()).sort().map(([state, n]) => (
            <span key={state} className={STATE_CLASS[state] ?? 'text-silver-muted'}>
              {n} {state}
            </span>
          ))}
        </div>
        {notBound.length === 0 ? (
          <p className="text-xs text-silver-muted">All {pvcs.length} volume claims are bound.</p>
        ) : (
          <ul className="divide-y divide-surface-elevated" data-testid="storage-summary-problems">
            {notBound.map((p) => (
              <li key={`${p.namespace}/${p.name}`} className="py-2 flex items-center gap-3 text-xs">
                <span className={`font-medium ${STATE_CLASS[p.status] ?? 'text-silver-muted'}`}>{p.status}</span>
                <span className="font-mono text-silver-bright">{p.name}</span>
                <span className="text-silver-muted">{p.namespace}</span>
                <span className="text-silver-muted">{p.storageClass || '—'}</span>
                <span className="text-silver-muted ml-auto">{p.age}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <section
      className="bg-surface border border-surface-elevated rounded-xl p-4 space-y-3"
      data-testid="storage-health-summary"
    >
      <div className="flex items-center gap-2">
        <Database size={14} className="text-silver-muted" />
        <h2 className="text-sm font-medium text-silver-bright">Storage</h2>
      </div>
      {body}
    </section>
  );
}
